import { Box, Flex, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import { useSetAtom } from 'jotai'
import {
  ArrowRightLeft,
  LogOut,
  Mail,
  MessageSquare,
  Pencil,
  PhoneCall,
  Sparkle,
} from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { userStateAtom } from '~/atoms/AuthenticationState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import {
  DropdownTextButton,
  TextButtonMenuItem,
} from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { InitialsAvatar } from '~/components/shared/InitialsAvatar'
import { SwitchPbxModal } from '~/components/shared/PbxPicker'
import { RegionIndicator } from '~/components/shared/RegionIndicator'
import { TextButton } from '~/components/shared/TextButton'
import { useColorMode } from '~/components/ui/color-mode'
import { toaster } from '~/components/ui/toaster'
import { Tooltip } from '~/components/ui/tooltip'
import { MANGO_SUPPORT_PHONE } from '~/constants/support'
import urls from '~/constants/urls'
import { useChangelogModal } from '~/hooks/useChangelogModal'
import { useLogout } from '~/hooks/useLogout'
import { DesktopAppSettings } from '~/pages/Settings/components/DesktopAppSettings'
import { useProfileSettingsStyle } from '~/pages/Settings/componentStyles/ProfileSettings.style'
import { useSharedSettingsStyle } from '~/pages/Settings/componentStyles/SharedSettings.style'
import { useGetPbxList } from '~/queries/pbxQueries'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'
import { createWebAppUserApi } from '~/utils/userApiAdapter'

import {
  EditMyProfileModal,
  type UpdateMyProfilePayload,
  useMyProfile,
} from '@mangovoice/users'

export const ProfileSettings = () => {
  const { t, i18n } = useTranslation()
  const styles = useProfileSettingsStyle()
  const sharedStyles = useSharedSettingsStyle()
  const handleLogout = useLogout()
  const { pbx, sessionUser } = useUserState()
  const setUserState = useSetAtom(userStateAtom)

  const handleProfileUpdated = useCallback(
    (payload: UpdateMyProfilePayload) => {
      setUserState((prev) => ({
        ...prev,
        sessionUser:
          prev.sessionUser != null
            ? {
                ...prev.sessionUser,
                first_name: payload.first_name,
                last_name: payload.last_name,
              }
            : prev.sessionUser,
      }))
    },
    [setUserState],
  )

  const [isSwitchPbxModalOpen, setIsSwitchPbxModalOpen] = useState(false)
  const { showChangelog, ChangelogModal } = useChangelogModal()
  const { settings, setPreferredLanguage } = useSettingsState()
  const { colorModePreference, setColorMode } = useColorMode()

  const { data: pbxList } = useGetPbxList({
    apiParams: { limit: 1 },
  })

  const userApi = useMemo(
    () => createWebAppUserApi(sessionUser ?? undefined),
    [sessionUser],
  )

  const { data: profile } = useMyProfile({
    api: userApi,
    userId: sessionUser?.id ?? 0,
  })

  const handleCallSupport = () => {
    openInNewTab(`tel:${MANGO_SUPPORT_PHONE.digits}`)
  }

  const handleEmailSupport = () => {
    openInNewTab('mailto:support@mangovoice.com')
  }

  const handleSendFeedback = () => {
    openInNewTab(urls.FEEDBACK)
  }

  const availableLanguages = [
    { value: undefined, label: t('languageSelector.autoDetect') },
    { value: 'en', label: t('languageSelector.languages.en') },
    { value: 'en-US', label: t('languageSelector.languages.en-US') },
    { value: 'en-GB', label: t('languageSelector.languages.en-GB') },
    { value: 'en-CA', label: t('languageSelector.languages.en-CA') },
    { value: 'es', label: t('languageSelector.languages.es') },
    { value: 'es-MX', label: t('languageSelector.languages.es-MX') },
    { value: 'es-US', label: t('languageSelector.languages.es-US') },
    { value: 'fr-CA', label: t('languageSelector.languages.fr-CA') },
    { value: 'ko', label: t('languageSelector.languages.ko') },
    { value: 'ne', label: t('languageSelector.languages.ne') },
    { value: 'te', label: t('languageSelector.languages.te') },
  ]

  const handleLanguageChange = useCallback(
    (lang: string | undefined) => {
      setPreferredLanguage(lang)
      if (lang) {
        void i18n.changeLanguage(lang)
      } else {
        // Auto-detect from system
        void i18n.changeLanguage(window.navigator.language)
      }
    },
    [i18n, setPreferredLanguage],
  )

  const languageMenuItems = useMemo(() => {
    return availableLanguages.map((lang) => {
      return {
        content: (
          <MenuListItem key={lang.value ?? 'auto'} primaryText={lang.label} />
        ),
        value: lang.value,
      } as TextButtonMenuItem<string | number | object>
    })
  }, [availableLanguages])

  const currentLanguageLabel = useMemo(() => {
    const selectedLang = availableLanguages.find(
      (lang) => lang.value === settings?.language?.preferredLanguage,
    )
    return selectedLang?.label ?? availableLanguages[0].label // Default to auto-detect
  }, [settings?.language?.preferredLanguage, availableLanguages])

  const handleShowChangelog = () => {
    showChangelog()
  }

  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false)

  return (
    <Flex css={sharedStyles.wrapper}>
      <Flex css={styles.wrapper}>
        <Flex css={styles.profileCard}>
          <Flex css={styles.nameWrapper}>
            <Box
              position="relative"
              display="inline-flex"
              borderRadius="full"
              cursor={sessionUser ? 'pointer' : 'default'}
              onClick={() => sessionUser && setEditProfileModalOpen(true)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if ((e.key === 'Enter' || e.key === ' ') && sessionUser) {
                  e.preventDefault()
                  setEditProfileModalOpen(true)
                }
              }}
              role="button"
              tabIndex={sessionUser ? 0 : -1}
              aria-label={t('profileSettings.editProfile', 'Edit profile')}
            >
              <InitialsAvatar
                firstName={sessionUser?.first_name}
                lastName={sessionUser?.last_name}
                size="lg"
              />
              {sessionUser && (
                <Tooltip
                  content={t('profileSettings.editProfile', 'Edit profile')}
                  placement="top"
                >
                  <Flex
                    position="absolute"
                    bottom={0}
                    right={0}
                    borderRadius="full"
                    bg="bg.panel"
                    borderWidth="2px"
                    borderColor="border.default"
                    boxShadow="sm"
                    align="center"
                    justify="center"
                    width={8}
                    height={8}
                  >
                    <Icon
                      as={Pencil}
                      boxSize={4}
                      color="fg.muted"
                      aria-hidden
                    />
                  </Flex>
                </Tooltip>
              )}
            </Box>
            <Text css={styles.nameText}>
              {sessionUser?.first_name} {sessionUser?.last_name}
            </Text>
          </Flex>
          {sessionUser && (
            <>
              <Flex id="my-profile" css={styles.helpSection}>
                <Flex css={styles.helpTitleWrapper}>
                  <Text css={styles.helpTitleText}>
                    {t('profileSettings.myProfile', 'My Profile')}
                  </Text>
                </Flex>
                <Flex flexDirection="column" gap="0.25rem">
                  <Text
                    fontSize="12px"
                    fontStyle="normal"
                    fontWeight="400"
                    lineHeight="16px"
                    color="fg.subtle"
                  >
                    {t('profileSettings.name', 'Name')}
                  </Text>
                  <Text css={styles.headerPbxText}>
                    {profile
                      ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() ||
                        '—'
                      : '—'}
                  </Text>
                </Flex>
                <Flex flexDirection="column" gap="0.25rem">
                  <Text
                    fontSize="12px"
                    fontStyle="normal"
                    fontWeight="400"
                    lineHeight="16px"
                    color="fg.subtle"
                  >
                    {t('profileSettings.username', 'Username')}
                  </Text>
                  <Text css={styles.headerPbxText}>
                    {profile?.username ?? '—'}
                  </Text>
                </Flex>
                <Flex flexDirection="column" gap="0.25rem">
                  <Text
                    fontSize="12px"
                    fontStyle="normal"
                    fontWeight="400"
                    lineHeight="16px"
                    color="fg.subtle"
                  >
                    {t('profileSettings.email', 'Email')}
                  </Text>
                  <Text css={styles.headerPbxText}>
                    {profile?.email ?? '—'}
                  </Text>
                </Flex>
              </Flex>
              <EditMyProfileModal
                open={editProfileModalOpen}
                onClose={() => setEditProfileModalOpen(false)}
                api={userApi}
                userId={sessionUser.id}
                profile={profile ?? null}
                toast={toaster}
                onProfileUpdated={handleProfileUpdated}
                labels={{
                  title: t(
                    'profileSettings.editMyProfile.title',
                    'Edit My Profile',
                  ),
                  update: t(
                    'profileSettings.editMyProfile.update',
                    'Update Profile',
                  ),
                  close: t('profileSettings.editMyProfile.close', 'Close'),
                  firstName: t(
                    'profileSettings.editMyProfile.firstName',
                    'First Name',
                  ),
                  lastName: t(
                    'profileSettings.editMyProfile.lastName',
                    'Last Name',
                  ),
                  username: t(
                    'profileSettings.editMyProfile.username',
                    'Username',
                  ),
                  email: t('profileSettings.editMyProfile.email', 'Email'),
                  errRequiredFirstName: t(
                    'profileSettings.editMyProfile.errRequiredFirstName',
                    'Please make an entry for the First Name.',
                  ),
                  errRequiredLastName: t(
                    'profileSettings.editMyProfile.errRequiredLastName',
                    'Please make an entry for the Last Name.',
                  ),
                  successProfile: t(
                    'profileSettings.editMyProfile.successProfile',
                    'Profile successfully updated.',
                  ),
                }}
              />
            </>
          )}
          <Flex id="pbx" css={styles.headerPbx}>
            <Text
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="16px"
              color="fg.subtle"
            >
              {t('profileSettings.pbxName')}
            </Text>
            <Text css={styles.headerPbxText}>{pbx?.name}</Text>
            {(pbxList?.count ?? 0) > 1 && (
              <TextButton
                variant={TextButtonVariants.Info}
                textButtonProps={{
                  justifyContent: 'flex-start',
                }}
                leftIcon={ArrowRightLeft}
                onLinkClick={() => setIsSwitchPbxModalOpen(true)}
              >
                {t('profileSettings.switchPbx')}
              </TextButton>
            )}
          </Flex>
          {!isElectron && (
            <Box id="desktop-app" width="100%">
              <DesktopAppSettings />
            </Box>
          )}
          {isElectron && (
            <Flex id="region" css={styles.helpSection}>
              <Flex css={styles.helpTitleWrapper}>
                <Text css={styles.helpTitleText}>
                  {t('regionSelector.settingsTitle')}
                </Text>
              </Flex>
              <RegionIndicator variant={'both'} />
              <Text
                fontSize=".85rem"
                fontStyle="italic"
                fontWeight="400"
                color="fg.muted"
              >
                {t('regionSelector.settingsDescription')}
              </Text>
            </Flex>
          )}
          <Flex id="dark-mode" css={styles.helpSection}>
            <Flex css={styles.helpTitleWrapper}>
              <Text css={styles.helpTitleText}>
                {t('profileSettings.darkMode')}
              </Text>
            </Flex>
            <Text fontSize=".875rem" fontWeight="500" marginBottom=".5rem">
              {colorModePreference === 'light'
                ? t('profileSettings.darkModeOptions.light')
                : colorModePreference === 'dark'
                  ? t('profileSettings.darkModeOptions.dark')
                  : t('profileSettings.darkModeOptions.system')}
            </Text>
            <DropdownSelect
              items={[
                {
                  content: (
                    <MenuListItem
                      key="light"
                      primaryText={t('profileSettings.darkModeOptions.light')}
                    />
                  ),
                  selectedLabel: t('profileSettings.darkModeOptions.light'),
                  value: 'light',
                },
                {
                  content: (
                    <MenuListItem
                      key="dark"
                      primaryText={t('profileSettings.darkModeOptions.dark')}
                    />
                  ),
                  selectedLabel: t('profileSettings.darkModeOptions.dark'),
                  value: 'dark',
                },
                {
                  content: (
                    <MenuListItem
                      key="system"
                      primaryText={t('profileSettings.darkModeOptions.system')}
                    />
                  ),
                  selectedLabel: t('profileSettings.darkModeOptions.system'),
                  value: 'system',
                },
              ]}
              selectedValue={colorModePreference}
              onSelect={(value) => {
                setColorMode(value as 'light' | 'dark' | 'system')
              }}
            />
          </Flex>
          <Flex id="language" css={styles.helpSection}>
            <Flex css={styles.helpTitleWrapper}>
              <Text css={styles.helpTitleText}>
                {t('profileSettings.language')}
              </Text>
            </Flex>
            <Text fontSize=".875rem" fontWeight="500" marginBottom=".5rem">
              {currentLanguageLabel}
            </Text>
            <DropdownTextButton
              placeholder={t('languageSelector.selectLanguage')}
              items={languageMenuItems}
              onSelect={(value) =>
                handleLanguageChange(value as string | undefined)
              }
              menuOptions={{
                width: '25rem',
              }}
              variant={TextButtonVariants.Info}
            />
          </Flex>
          <Flex id="help" css={styles.helpSection}>
            <Flex css={styles.helpTitleWrapper}>
              <Text css={styles.helpTitleText}>
                {t('profileSettings.needHelp')}
              </Text>
            </Flex>
            <TextButton
              variant={TextButtonVariants.Info}
              leftIcon={PhoneCall}
              onLinkClick={handleCallSupport}
            >
              {t('profileSettings.callSupport')} {MANGO_SUPPORT_PHONE.formatted}
            </TextButton>
            <TextButton
              variant={TextButtonVariants.Info}
              leftIcon={Mail}
              onLinkClick={handleEmailSupport}
            >
              {t('profileSettings.emailSupport')}
            </TextButton>
            <TextButton
              variant={TextButtonVariants.Info}
              leftIcon={MessageSquare}
              onLinkClick={handleSendFeedback}
            >
              {t('profileSettings.sendFeedback')}
            </TextButton>
            {isElectron && (
              <TextButton leftIcon={Sparkle} onLinkClick={handleShowChangelog}>
                {t('profileSettings.whatsNew')}
              </TextButton>
            )}
          </Flex>
          <Flex css={styles.headerActions}>
            <TextButton
              variant={TextButtonVariants.Info}
              leftIcon={LogOut}
              onLinkClick={handleLogout}
            >
              {t('profileSettings.logOut')}
            </TextButton>
          </Flex>
        </Flex>
      </Flex>
      {ChangelogModal}
      <SwitchPbxModal
        open={isSwitchPbxModalOpen}
        onClose={() => setIsSwitchPbxModalOpen(false)}
      />
    </Flex>
  )
}
